import {
  type ExamImage,
  type ExamWorksheetData,
  isMultiIndicationWorksheet,
  isProcedureWorksheet,
  isUsWorksheet,
  type ProcedureFindingValue,
  type ProcedureWorksheetData,
  type UsFindingValue,
  type UsWorksheetData,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { isAortaComplete } from "./aorta-worksheet";
import { isBladderComplete } from "./bladder-worksheet";
import { isCardiacComplete } from "./cardiac-worksheet";
import { isEfastComplete } from "./efast-worksheet";
import { isLungComplete } from "./lung-worksheet";
import { isMskComplete } from "./msk-regions";
import { isPregnancyComplete } from "./pregnancy-worksheet";
import {
  isProcedureComplete,
  PROCEDURE_DEFINITIONS,
} from "./procedures/procedure-definitions";
import { isRenalComplete } from "./renal-worksheet";
import {
  effectiveIndication,
  isUsAllNormal,
  isUsComplete,
  usDefinitionFor,
  visibleFields,
  visibleInstances,
} from "./us/us-definitions";

export type SigningGateItem =
  | "INDICATION"
  | "FINDINGS"
  | "IMPRESSION"
  | "WORKSHEET"
  | "PROCEDURE_DETAILS"
  | "IMAGE"
  | "NEEDLE_IMAGE"
  | "CODE";

export const SIGNING_GATE_LABELS: Record<SigningGateItem, string> = {
  INDICATION: "An indication",
  FINDINGS: "A finding, All Normal or Technically limited",
  IMPRESSION: "An impression",
  WORKSHEET: "Every required worksheet field",
  PROCEDURE_DETAILS: "Every required procedure field",
  IMAGE: "An image or clip",
  NEEDLE_IMAGE: "An image marked needle-in-target",
  CODE: "A ticked billing code, or Non-billable",
};

export type SigningGateInput = {
  data: ExamWorksheetData;
  images: readonly ExamImage[];
  hasTickedCode: boolean;
  nonBillable: boolean;
};

const isAnswered = (
  value: UsFindingValue | ProcedureFindingValue | undefined,
): boolean =>
  Array.isArray(value)
    ? value.length > 0
    : value !== null && value !== undefined && value !== "";

const usItems = (data: UsWorksheetData): Array<[SigningGateItem, boolean]> => {
  const definition = usDefinitionFor(data.examType);
  if (!definition) return [["WORKSHEET", false]];
  const instances = visibleInstances(definition, data.findings);
  const answeredIn = (role: "findings" | "impression") =>
    instances
      .filter(({ section }) => section.role === role)
      .some((instance) =>
        visibleFields(instance.fields, data.findings).some(({ field }) =>
          isAnswered(data.findings[field.id]),
        ),
      );
  const technicallyLimited = instances.some(
    ({ section, limitedKey }) =>
      section.role === "findings" && data.technicallyLimited[limitedKey],
  );
  return [
    ["INDICATION", effectiveIndication(definition, data).length > 0],
    [
      "FINDINGS",
      answeredIn("findings") || isUsAllNormal(data) || technicallyLimited,
    ],
    ["IMPRESSION", answeredIn("impression")],
  ];
};

const procedureItems = (
  data: ProcedureWorksheetData,
  images: readonly ExamImage[],
): Array<[SigningGateItem, boolean]> => {
  const definition = PROCEDURE_DEFINITIONS[data.examType];
  const needleImageClaimed =
    data.findings.needle_in_target_image_saved === "yes";
  return [
    ["INDICATION", data.indication.length > 0],
    [
      "PROCEDURE_DETAILS",
      definition.completeWhenAnswered.every((id) =>
        isAnswered(data.findings[id]),
      ),
    ],
    [
      "NEEDLE_IMAGE",
      !needleImageClaimed || images.some((image) => image.needleInTarget),
    ],
  ];
};

export const isExamComplete = (
  data: ExamWorksheetData,
  images: readonly ExamImage[],
): boolean => {
  if (isUsWorksheet(data)) return isUsComplete(data);
  if (isProcedureWorksheet(data)) return isProcedureComplete(data, images);
  switch (data.examType) {
    case "cardiac":
      return isCardiacComplete(data);
    case "efast":
      return isEfastComplete(data);
    case "lung":
      return isLungComplete(data);
    case "bladder":
      return isBladderComplete(data);
    case "renal":
      return isRenalComplete(data);
    case "aorta":
      return isAortaComplete(data);
    case "pregnancy":
      return isPregnancyComplete(data);
    case "msk":
      return isMskComplete(data);
  }
};

const standardItems = (
  data: ExamWorksheetData,
  images: readonly ExamImage[],
): Array<[SigningGateItem, boolean]> => [
  [
    "INDICATION",
    isMultiIndicationWorksheet(data)
      ? data.indication.length > 0
      : !!data.indication,
  ],
  ["WORKSHEET", isExamComplete(data, images)],
];

export const missingForSigning = ({
  data,
  images,
  hasTickedCode,
  nonBillable,
}: SigningGateInput): SigningGateItem[] => {
  const familyItems = isUsWorksheet(data)
    ? usItems(data)
    : isProcedureWorksheet(data)
      ? procedureItems(data, images)
      : standardItems(data, images);
  const items: Array<[SigningGateItem, boolean]> = [
    ...familyItems,
    ["IMAGE", images.length > 0],
    ["CODE", hasTickedCode || nonBillable],
  ];
  return items.flatMap(([item, satisfied]) => (satisfied ? [] : [item]));
};
